import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Competition } from '../types';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { queryCache, CACHE_TTL } from '../lib/cache';

interface CompetitionsContextValue {
  competitions: Competition[];
  loading: boolean;
  error: string | null;
  refreshCompetitions: () => Promise<void>;
  getCompetitionById: (id: string) => Competition | undefined;
}

const CompetitionsContext = createContext<CompetitionsContextValue | undefined>(undefined);

export const useCompetitionsContext = () => {
  const context = useContext(CompetitionsContext);
  if (!context) {
    throw new Error('useCompetitionsContext must be used within CompetitionsProvider');
  }
  return context;
};

interface CompetitionsProviderProps {
  children: React.ReactNode;
}

export const CompetitionsProvider: React.FC<CompetitionsProviderProps> = ({ children }) => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentOrganization, user } = useAuth();
  const [lastOrgId, setLastOrgId] = React.useState<string | null>(null);

  const fetchCompetitions = useCallback(async () => {
    if (!currentOrganization?.id) {
      setCompetitions([]);
      setLoading(false);
      return;
    }

    // All client reads go through the `public_competitions` view, which excludes
    // sensitive financial / Stripe columns (stripe_product_id, stripe_price_id,
    // stripe_fee_*, platform_fee_*, gross_collected, total_*_fees,
    // total_organization_revenue, organization_revenue_percentage, etc.).
    // Admin-only screens fetch those fields separately via dedicated hooks/
    // edge functions that enforce admin role.
    const cacheKey = queryCache.generateKey('all_competitions', {
      orgId: currentOrganization.id,
      authenticated: !!user
    });

    try {
      setLoading(true);
      setError(null);

      const result = await queryCache.deduplicate(
        cacheKey,
        async () => {
          const pub = await supabase
            .from('public_competitions')
            .select('*')
            .eq('organization_id', currentOrganization.id)
            .order('created_at', { ascending: false });
          if (pub.error) throw pub.error;
          const data = pub.data;

          // Transform database rows to Competition type
          const transformed: Competition[] = (data || []).map((row: any) => ({
            id: row.id,
            organizationId: row.organization_id,
            name: row.name,
            competitionType: row.competition_type,
            startDate: new Date(row.start_date),
            endDate: row.end_date ? new Date(row.end_date) : null,
            status: row.status,
            isActive: row.is_active || false,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at),
            skippedGameweeks: row.skipped_gameweeks || [],
            endGameScenario: row.end_game_scenario || 'last_man_standing',
            missedPickBehavior: row.missed_pick_behavior || 'eliminate',
            entryFeeEnabled: row.entry_fee_enabled || false,
            entryFeeAmount: row.entry_fee_amount || 0,
            entryFeeCurrency: row.entry_fee_currency || 'gbp',
            // Prize pot fields
            calculatedPrizePot: row.calculated_prize_pot || 0,
            manualPrizePotOverride: row.manual_prize_pot_override,
            initialPrizePot: row.initial_prize_pot || 0,
            prizePotCurrency: row.prize_pot_currency || row.entry_fee_currency || 'gbp',
            // Revenue sharing fields
            organizationRevenuePercentage: row.organization_revenue_percentage,
            prizePotPercentage: row.prize_pot_percentage,
            stripeFeeToOrgPercentage: row.stripe_fee_to_org_percentage,
            // Calculated revenue amounts
            grossCollected: row.gross_collected,
            totalStripeFees: row.total_stripe_fees,
            totalPlatformFees: row.total_platform_fees,
            totalOrganizationRevenue: row.total_organization_revenue,
            netPrizePot: row.net_prize_pot,
            // Competition end state fields
            completionType: row.completion_type,
            winners: row.winners,
            finalGameweek: row.final_gameweek,
            rolledOver: row.rolled_over || false,
            rolledOverFrom: row.rolled_over_from,
            // Stripe and capacity fields
            stripeProductId: row.stripe_product_id,
            stripePriceId: row.stripe_price_id,
            maxParticipants: row.max_participants,
            // Predictor-specific fields (will be undefined for LMS competitions)
            prizeDistributionType: row.prize_distribution_type,
            topXPlaces: row.top_x_places,
            monthlyPrizesEnabled: row.monthly_prizes_enabled,
            monthlyMinFixtures: row.monthly_min_fixtures,
            // Multiple entries configuration
            allowMultipleEntries: row.allow_multiple_entries || false,
            maxEntriesPerUser: row.max_entries_per_user,
            // Differential pricing
            newEntrantFeeEnabled: row.new_entrant_fee_enabled || false,
            newEntrantFeeAmount: row.new_entrant_fee_amount
          }));

          return transformed;
        },
        CACHE_TTL.DEFAULT
      );

      // Ensure Date objects are properly restored from cache (cache serializes to JSON)
      // Also preserve all competition fields including multi-entry configuration
      const ensuredDates = result.map(comp => ({
        ...comp,
        startDate: comp.startDate instanceof Date ? comp.startDate : new Date(comp.startDate),
        endDate: comp.endDate ? (comp.endDate instanceof Date ? comp.endDate : new Date(comp.endDate)) : null,
        createdAt: comp.createdAt instanceof Date ? comp.createdAt : new Date(comp.createdAt),
        updatedAt: comp.updatedAt instanceof Date ? comp.updatedAt : new Date(comp.updatedAt),
        // Ensure multi-entry fields are preserved from cache
        allowMultipleEntries: comp.allowMultipleEntries ?? false,
        maxEntriesPerUser: comp.maxEntriesPerUser
      }));

      setCompetitions(ensuredDates);
    } catch (err) {
      console.error('Error fetching competitions:', err);
      setError('Failed to load competitions');
      setCompetitions([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id, user]);

  useEffect(() => {
    // Only fetch if organization changed
    if (currentOrganization?.id && currentOrganization.id !== lastOrgId) {
      setLastOrgId(currentOrganization.id);
      fetchCompetitions();
    } else if (!currentOrganization?.id && lastOrgId) {
      // Don't immediately clear - the organization might just be reloading
      // Only clear after a delay to avoid flashing empty states
      const timeoutId = setTimeout(() => {
        if (!currentOrganization?.id) {
          setLastOrgId(null);
          setCompetitions([]);
          setLoading(false);
        }
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [currentOrganization?.id, lastOrgId, fetchCompetitions]);

  const getCompetitionById = useCallback((id: string) => {
    return competitions.find(c => c.id === id);
  }, [competitions]);

  const value: CompetitionsContextValue = useMemo(() => ({
    competitions,
    loading,
    error,
    refreshCompetitions: fetchCompetitions,
    getCompetitionById
  }), [competitions, loading, error, fetchCompetitions, getCompetitionById]);

  return (
    <CompetitionsContext.Provider value={value}>
      {children}
    </CompetitionsContext.Provider>
  );
};
